/**
 * Constants for provider search functionality
 * Centralizes magic numbers and configuration values
 */

// Import centralized age brackets
import { AGE_BRACKETS } from "./ageBrackets";
import type { SearchFilters } from "../types/providerSearch";
import {
  ALL_BUSINESS_TYPES,
  REGISTRATION_BUSINESS_TYPE_LABELS,
} from "../generated/careTaxonomy";
export {
  ALL_AVAILABLE_SERVICES,
  ALL_BUSINESS_TYPES,
  BUSINESS_TYPE_DEFINITIONS,
  /** Full taxonomy registration list (includes service_tag keys). Prefer POLICY_FACILITY_LICENSE_REGISTRATION_OPTIONS / registration.ts for facility forms. */
  BUSINESS_TYPE_REGISTRATION_OPTIONS,
  PATIENT_NEED_OPTIONS,
  PREFERRED_SERVICES,
  PROVIDER_TYPES,
  REGISTRATION_BUSINESS_TYPE_LABELS,
  TAXONOMY_VERSION,
} from "../generated/careTaxonomy";

// Search configuration
export const SEARCH_CONFIG = {
  DEFAULT_RADIUS_MILES: 20,
  MIN_RADIUS_MILES: 1,
  MAX_RADIUS_MILES: 100,
  MIN_RATING: 1,
  MAX_RATING: 5,
  MIN_LOCATION_LENGTH: 3,
  /** Omit sort_by in API requests to use server-side automatic sort contract. */
  DEFAULT_SORT_BY: undefined as string | undefined,
  DEFAULT_SORT_ORDER: undefined as "asc" | "desc" | undefined,
  CACHE_VALIDITY_MS: 60 * 60 * 1000, // 1 hour
  /** Matches GET /search/providers/public default limit query param. */
  DEFAULT_PROVIDER_RESULT_LIMIT: 50,
  MAX_PROVIDER_RESULT_LIMIT: 100,
} as const;

/** True when search expresses care-matching intent (matches backend sort contract). */
export function hasCareMatchingIntent(
  filters: Partial<{
    patient_needs?: string[];
    required_core_services?: string[];
    required_service_keys?: string[];
    nice_to_have_core_services?: string[];
    specializations?: string[];
  }>
): boolean {
  const lists = [
    filters.patient_needs,
    filters.required_core_services,
    filters.required_service_keys,
    filters.specializations,
  ];
  return lists.some(
    (items) =>
      Array.isArray(items) &&
      items.some((item) => typeof item === "string" && item.trim().length > 0)
  );
}

/**
 * Persist-rehydrate hook for search filters.
 *
 * Do **not** strip ``distance``/``asc``: that is the results-header
 * "Distance (nearest)" preset. Clearing it on every rehydrate made an
 * explicit Distance choice look like Automatic after refresh.
 */
export function migrateLegacySearchFilters(
  filters: SearchFilters
): SearchFilters {
  return { ...filters };
}

export const SORT_BY_OPTIONS = [
  { value: "", label: "Automatic" },
  { value: "relevance", label: "Best match" },
  { value: "distance", label: "Distance" },
  { value: "rating", label: "Rating" },
  { value: "availability", label: "Availability" },
  { value: "name", label: "Name" },
] as const;

export const SORT_ORDER_OPTIONS = [
  { value: "", label: "Automatic" },
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
] as const;

// Search modes
export const SEARCH_MODES = {
  BASIC: "basic",
  ADVANCED: "advanced",
  RECOMMENDED: "recommended",
} as const;

/** Availability filter labels for provider search (plain text for selects). */
export const AVAILABILITY_FILTER_ANY_LABEL = "Any availability";
export const AVAILABILITY_FILTER_ACCEPTING_LABEL = "Accepting new residents";
export const AVAILABILITY_FILTER_NOT_ACCEPTING_LABEL =
  "Not accepting new residents";

/** Short map-legend labels; pair with AVAILABILITY_FILTER_* in tooltips/aria. */
export const MAP_LEGEND_ACCEPTING_LABEL = "Accepting";
export const MAP_LEGEND_NOT_ACCEPTING_LABEL = "Not accepting";

export const AVAILABILITY_FILTER_HELP =
  "Show all providers, or limit to those accepting new residents.";

// Error messages
export const SEARCH_ERRORS = {
  LOCATION_REQUIRED: "Add a ZIP or address to search",
  LOCATION_TOO_SHORT: "Location must be at least 3 characters long",
  INVALID_RADIUS: "Radius must be between 1 and 100 miles",
  INVALID_RATING: "Minimum rating must be between 1 and 5",
  SEARCH_FAILED: "Failed to search providers",
  LOAD_FAILED: "Failed to load providers",
  INVALID_PROVIDER_ID: "Invalid provider ID",
  REQUEST_TIMEOUT: "Request timeout",
} as const;

// Request configuration
export const REQUEST_CONFIG = {
  // Provider search can take 30–45s when geocoding is slow; align with publicApi 60s timeout
  TIMEOUT_MS: 60000, // 60 seconds for location-based search (geocoding + DB)
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1000,
} as const;

// Search options and UI constants
export const RADIUS_OPTIONS = [
  { value: 5, label: "5 miles" },
  { value: 10, label: "10 miles" },
  { value: 20, label: "20 miles" },
  { value: 50, label: "50 miles" },
  { value: 100, label: "100 miles" },
] as const;

export const RATING_OPTIONS = [
  { value: 1, label: "1+ stars" },
  { value: 2, label: "2+ stars" },
  { value: 3, label: "3+ stars" },
  { value: 4, label: "4+ stars" },
  { value: 5, label: "5 stars" },
] as const;

export const INSURANCE_PLANS = [
  "Medicaid",
  "Medicare",
  "Private Insurance",
  "Veterans Benefits",
  "Long-term Care Insurance",
] as const;

export const BUDGET_RANGES = [
  { min: 0, max: 2000, label: "Under $2,000/month" },
  { min: 2000, max: 4000, label: "$2,000 - $4,000/month" },
  { min: 4000, max: 6000, label: "$4,000 - $6,000/month" },
  { min: 6000, max: 8000, label: "$6,000 - $8,000/month" },
  { min: 8000, max: 10000, label: "$8,000 - $10,000/month" },
  { min: 10000, max: Infinity, label: "Over $10,000/month" },
] as const;

// Re-export for backwards compatibility
export const AGE_GROUPS = AGE_BRACKETS;

export const BUSINESS_TYPE_MAPPINGS = Object.fromEntries(
  ALL_BUSINESS_TYPES.map((key) => [key, REGISTRATION_BUSINESS_TYPE_LABELS[key]])
) as Record<(typeof ALL_BUSINESS_TYPES)[number], string>;

export const STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export const MEDICAL_CONDITIONS = [
  "Alzheimer's Disease",
  "Dementia",
  "Parkinson's Disease",
  "Stroke",
  "Diabetes",
  "Heart Disease",
  "Arthritis",
  "Mobility Issues",
  "Vision Impairment",
  "Hearing Impairment",
  "Depression",
  "Anxiety",
  "Other",
] as const;

export const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Chinese",
  "Japanese",
  "Korean",
  "Arabic",
  "Russian",
  "Other",
] as const;

// AGE_GROUP_OPTIONS removed - use AGE_GROUPS instead

export {
  DISABILITY_CARE_CONDITION_OPTIONS as DISABILITY_SUPPORT_OPTIONS,
  HANDICAP_ACCESSIBILITY_FEATURE_OPTIONS as ACCESSIBILITY_FEATURES,
} from "./providerCapabilityTaxonomy";

export const SECTION_COLORS = {
  primary: "bg-blue-50 border-blue-200",
  secondary: "bg-gray-50 border-gray-200",
  success: "bg-green-50 border-green-200",
  warning: "bg-yellow-50 border-yellow-200",
  error: "bg-red-50 border-red-200",
  disability: "bg-purple-50 border-purple-200",
} as const;

export const SECTION_ICON_COLORS = {
  primary: "text-blue-600",
  secondary: "text-gray-600",
  success: "text-green-600",
  warning: "text-yellow-600",
  error: "text-red-600",
} as const;

// Type definitions for better type safety
export type SearchMode = (typeof SEARCH_MODES)[keyof typeof SEARCH_MODES];
export type SortOrder = "asc" | "desc";
export type SortBy = "distance" | "rating" | "name" | "availability" | "relevance";
